/**
 * Small, non-sensitive marker cookie for the courses admin-preview session.
 *
 * The admin JWT lives in `sessionStorage` under `acatv2` (client-only, see
 * `admin-preview-token.utils.ts`) since it's too large for a cookie, but that's invisible to
 * the server — on a full reload the SSR routing guards can't tell a preview is active and
 * would bounce to `/login` before the client re-hydrates. This marker cookie fixes that: on
 * token exchange we persist a tiny marker ({@link AdminPreviewMarker} — `locationId` + `exp`,
 * NO token) the server reads from the `Cookie` header to render the `/courses` shell on SSR
 * and defer real auth (still `acatv2`-gated) to the client.
 *
 * Cookie flags mirror the learner auth cookie (`SameSite=None; Secure`) for the cross-site HMI
 * builder iframe on clientclub, plus `Partitioned` (CHIPS) so browsers that block unpartitioned
 * third-party cookies (Safari ITP, Chrome 3PCD) still keep it inside the builder iframe. TTL mirrors
 * membership-hmi-preview PR #1321 (~1d).
 *
 * SSR-only signal: client-side request auth never reads this cookie (it is browser-wide, so it
 * cannot say which TAB is previewing which location) — that is the per-tab `cp-admin-preview`
 * record in `admin-preview-token.utils.ts`.
 */
import Cookies from 'js-cookie'

/** Dedicated cookie name for the courses admin-preview marker (courses-admin-preview-marker). */
export const ADMIN_PREVIEW_MARKER_COOKIE_NAME = 'capm' as const

/** Fallback TTL when the exchange envelope carries no usable expiry (mirrors PR #1321 `1d`). */
const DEFAULT_MARKER_TTL_MS = 24 * 60 * 60 * 1000

/** Attributes shared by write + remove: a `Partitioned` cookie is only removed with matching attributes. */
const MARKER_COOKIE_ATTRIBUTES = { path: '/', secure: true, sameSite: 'None', partitioned: true } as const

/** Non-sensitive marker persisted in the `capm` cookie — deliberately excludes the admin token, only says "preview active for this location until `exp`". */
export interface AdminPreviewMarker {
  locationId: string
  /** Expiry as a millisecond epoch timestamp. */
  exp: number
}

function encodeMarker(marker: AdminPreviewMarker): string {
  const json = JSON.stringify(marker)
  return typeof btoa !== 'undefined' ? btoa(json) : Buffer.from(json).toString('base64')
}

/** Decode a base64-JSON marker. Returns null for anything that is not a well-formed marker. */
export function decodeAdminPreviewMarker(raw: string | null | undefined): AdminPreviewMarker | null {
  if (!raw) return null
  try {
    const decoded = typeof atob !== 'undefined' ? atob(raw) : Buffer.from(raw, 'base64').toString('utf-8')
    const parsed = JSON.parse(decoded) as unknown
    if (!parsed || typeof parsed !== 'object') return null
    const { locationId, exp } = parsed as Partial<AdminPreviewMarker>
    if (typeof locationId !== 'string' || !locationId) return null
    if (typeof exp !== 'number' || Number.isNaN(exp)) return null
    return { locationId, exp }
  } catch {
    // A parse failure just means "not our marker" — non-fatal.
    return null
  }
}

/** A marker is valid when it is well-formed and its expiry is still in the future. */
export function isAdminPreviewMarkerValid(marker: AdminPreviewMarker | null): boolean {
  if (!marker) return false
  return marker.exp > Date.now()
}

/** Normalise an envelope `tokenExpirationTime` (ms epoch, `number | string`) to a future ms epoch. */
function normalizeExpiry(expiresAt?: number | string | null): number {
  if (expiresAt != null) {
    const parsed = typeof expiresAt === 'number' ? expiresAt : parseInt(expiresAt, 10)
    if (!Number.isNaN(parsed) && parsed > Date.now()) return parsed
  }
  return Date.now() + DEFAULT_MARKER_TTL_MS
}

/**
 * Client-only: persist the marker cookie after a successful admin-token exchange. No-op on SSR.
 * `expiresAt` is the envelope's `tokenExpirationTime`; falls back to the default TTL if missing/invalid.
 */
export function writeAdminPreviewMarkerCookie(input: { locationId: string; expiresAt?: number | string | null }): void {
  if (typeof document === 'undefined' || !input.locationId) return
  const exp = normalizeExpiry(input.expiresAt)
  Cookies.set(ADMIN_PREVIEW_MARKER_COOKIE_NAME, encodeMarker({ locationId: input.locationId, exp }), {
    ...MARKER_COOKIE_ATTRIBUTES,
    expires: new Date(exp),
  })
}

/** Client-only: read + decode the marker cookie. Returns null on SSR or when absent/invalid. */
export function readAdminPreviewMarkerCookie(): AdminPreviewMarker | null {
  if (typeof document === 'undefined') return null
  return decodeAdminPreviewMarker(Cookies.get(ADMIN_PREVIEW_MARKER_COOKIE_NAME) ?? null)
}

/** Client-only: clear the marker cookie (logout / explicit reset). No-op on SSR. */
export function clearAdminPreviewMarkerCookie(): void {
  if (typeof document === 'undefined') return
  Cookies.remove(ADMIN_PREVIEW_MARKER_COOKIE_NAME, MARKER_COOKIE_ATTRIBUTES)
  // Markers written before `Partitioned` was added live in the unpartitioned jar.
  Cookies.remove(ADMIN_PREVIEW_MARKER_COOKIE_NAME, { path: '/' })
}

/**
 * Client-only: clear the marker only when it still points at `locationId` — the location the
 * exiting tab previewed. The cookie is browser-wide, so a tab leaving its preview must not wipe
 * another tab's marker for a different location. No-op on SSR / without a location.
 */
export function clearAdminPreviewMarkerCookieForLocation(locationId: string | null | undefined): void {
  if (!locationId) return
  const marker = readAdminPreviewMarkerCookie()
  if (marker && marker.locationId !== locationId) return
  clearAdminPreviewMarkerCookie()
}

/** Extract a single cookie value from a raw `Cookie` request header. */
function readCookieValueFromHeader(cookieHeader: string, name: string): string | null {
  for (const segment of cookieHeader.split(';')) {
    const trimmed = segment.trim()
    if (!trimmed) continue
    const separatorIndex = trimmed.indexOf('=')
    if (separatorIndex === -1) continue
    if (trimmed.slice(0, separatorIndex).trim() !== name) continue
    const rawValue = trimmed.slice(separatorIndex + 1)
    try {
      return decodeURIComponent(rawValue)
    } catch {
      return rawValue
    }
  }
  return null
}

/** Server-side: read + decode the marker from an incoming request `Cookie` header. */
export function readAdminPreviewMarkerFromCookieHeader(cookieHeader: string | string[] | undefined): AdminPreviewMarker | null {
  const header = Array.isArray(cookieHeader) ? cookieHeader.join('; ') : cookieHeader
  if (!header) return null
  return decodeAdminPreviewMarker(readCookieValueFromHeader(header, ADMIN_PREVIEW_MARKER_COOKIE_NAME))
}

/** Whether a valid admin-preview marker is present: client reads the `capm` document cookie (ignores `ssrCookieHeader`), server decodes `ssrCookieHeader` (pass the request `Cookie` header). */
export function hasActiveAdminPreviewMarker(ssrCookieHeader?: string | string[]): boolean {
  if (typeof document !== 'undefined') {
    return isAdminPreviewMarkerValid(readAdminPreviewMarkerCookie())
  }
  return isAdminPreviewMarkerValid(readAdminPreviewMarkerFromCookieHeader(ssrCookieHeader))
}
